import React, { useState, useEffect } from 'react';
import { Music, ChevronRight, AlertCircle } from 'lucide-react';

/**
 * Track Selector Component
 * Allows selection of melody track from MIDI file
 */
export default function TrackSelector({ midiData, onTrackSelect, onBack }) {
  const [tracks, setTracks] = useState([]);
  const [suggestedTrack, setSuggestedTrack] = useState(null);

  useEffect(() => {
    if (!midiData?.midi) return;

    // Analyze tracks
    const analyzedTracks = midiData?.midi?.tracks?.map((track, index) => {
      const notes = track?.notes || [];
      const noteCount = notes?.length;
      
      if (noteCount === 0) {
        return {
          index,
          name: track?.name || `Track ${index + 1}`,
          noteCount: 0,
          avgPitch: 0,
          range: { low: 0, high: 0 },
          duration: 0,
          isMelody: false
        };
      }

      const midiNotes = notes?.map(n => n?.midi);
      const avgPitch = midiNotes?.reduce((sum, m) => sum + m, 0) / midiNotes?.length;
      const low = Math.min(...midiNotes);
      const high = Math.max(...midiNotes);
      const duration = track?.duration;

      // Heuristic: melody likely in mid-vocal range (C3-C6, MIDI 48-84)
      const vocalsRangeScore = midiNotes?.filter(m => m >= 48 && m <= 84)?.length / noteCount;
      const isMelody = vocalsRangeScore > 0.7 && noteCount > 10;

      return {
        index,
        name: track?.name || `Track ${index + 1}`,
        noteCount,
        avgPitch,
        range: { low, high },
        duration,
        isMelody,
        vocalsRangeScore
      };
    })?.filter(t => t?.noteCount > 0); // Remove empty tracks

    setTracks(analyzedTracks);

    // Find suggested track
    const suggested = analyzedTracks?.reduce((best, current) => {
      if (!best) return current;
      // Prefer tracks with more notes in vocal range
      if (current?.vocalsRangeScore > best?.vocalsRangeScore) return current;
      if (current?.vocalsRangeScore === best?.vocalsRangeScore && current?.noteCount > best?.noteCount) return current;
      return best;
    }, null);

    setSuggestedTrack(suggested);
  }, [midiData]);

  const midiToNoteName = (midi) => {
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octave = Math.floor(midi / 12) - 1;
    const note = noteNames?.[midi % 12];
    return `${note}${octave}`;
  };

  if (tracks?.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
          <p className="text-xl font-medium text-gray-800 mb-2">No Valid Tracks Found</p>
          <p className="text-gray-600 mb-6">The MIDI file doesn't contain any note data.</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
          >
            Upload Different File
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <Music className="w-7 h-7 text-purple-600" />
        Select Melody Track
      </h2>
      {/* Suggested Track */}
      {suggestedTrack && (
        <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
          <p className="text-sm font-medium text-green-800 mb-1">✨ Recommended Track</p>
          <p className="text-sm text-green-700">
            Track {suggestedTrack?.index + 1} appears to be the melody line based on note range and density.
          </p>
        </div>
      )}
      {/* Track List */}
      <div className="space-y-4">
        {tracks?.map((track) => (
          <button
            key={track?.index}
            onClick={() => onTrackSelect(track)}
            className={`w-full p-6 rounded-xl border-2 transition-all text-left hover:shadow-lg ${
              suggestedTrack?.index === track?.index
                ? 'border-green-500 bg-green-50 hover:border-green-600' :'border-gray-200 hover:border-purple-400'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold text-gray-800">{track?.name}</h3>
                  {suggestedTrack?.index === track?.index && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                      SUGGESTED
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div>
                    <p className="font-medium">Notes</p>
                    <p className="text-gray-800">{track?.noteCount}</p>
                  </div>
                  <div>
                    <p className="font-medium">Range</p>
                    <p className="text-gray-800">
                      {midiToNoteName(track?.range?.low)} - {midiToNoteName(track?.range?.high)}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium">Duration</p>
                    <p className="text-gray-800">{Math.round(track?.duration)}s</p>
                  </div>
                  <div>
                    <p className="font-medium">Avg Pitch</p>
                    <p className="text-gray-800">{midiToNoteName(Math.round(track?.avgPitch))}</p>
                  </div>
                </div>
              </div>

              <ChevronRight className="w-6 h-6 text-gray-400 flex-shrink-0 ml-4" />
            </div>
          </button>
        ))}
      </div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="mt-6 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
      >
        Upload Different File
      </button>
    </div>
  );
}